import { readBase64 } from "./fileApi";

export type ExportFormat = "png" | "jpeg" | "webp";

export interface Point {
  x: number;
  y: number;
}

export interface Placement {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface LayerGeometry {
  placement: Placement;
  perspectiveEnabled: boolean;
  quad: [Point, Point, Point, Point] | null;
}

export interface ComposeLayer {
  image: HTMLImageElement;
  visible: boolean;
  opacity: number;
  geometry: LayerGeometry;
}

const imagePathCache = new Map<string, Promise<HTMLImageElement>>();

function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });
}

export async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  // Keep blob URL alive while editor preview is using img.src.
  // URL lifecycle is managed by App when layers/template are replaced or disposed.
  return loadImageFromUrl(url);
}

export async function loadImageFromPath(path: string): Promise<HTMLImageElement> {
  const cached = imagePathCache.get(path);
  if (cached) {
    return cached;
  }
  const task = (async () => {
    const base64 = await readBase64(path);
    return loadImageFromUrl(`data:image/*;base64,${base64}`);
  })();
  imagePathCache.set(path, task);
  return task;
}

function contentTypeForFormat(format: ExportFormat) {
  if (format === "jpeg") {
    return "image/jpeg";
  }
  if (format === "webp") {
    return "image/webp";
  }
  return "image/png";
}

function bilinear(quad: [Point, Point, Point, Point], u: number, v: number): Point {
  const [p00, p10, p11, p01] = quad;
  return {
    x: (1 - u) * (1 - v) * p00.x + u * (1 - v) * p10.x + u * v * p11.x + (1 - u) * v * p01.x,
    y: (1 - u) * (1 - v) * p00.y + u * (1 - v) * p10.y + u * v * p11.y + (1 - u) * v * p01.y
  };
}

function computeAffineFromTriangles(src: [Point, Point, Point], dst: [Point, Point, Point]) {
  const [s0, s1, s2] = src;
  const [d0, d1, d2] = dst;
  const det = s0.x * (s1.y - s2.y) - s0.y * (s1.x - s2.x) + s1.x * s2.y - s2.x * s1.y;
  if (Math.abs(det) < 1e-8) {
    return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  }

  const a = (d0.x * (s1.y - s2.y) - d1.x * (s0.y - s2.y) + d2.x * (s0.y - s1.y)) / det;
  const b = (d0.y * (s1.y - s2.y) - d1.y * (s0.y - s2.y) + d2.y * (s0.y - s1.y)) / det;
  const c = (d0.x * (s2.x - s1.x) + d1.x * (s0.x - s2.x) + d2.x * (s1.x - s0.x)) / det;
  const d = (d0.y * (s2.x - s1.x) + d1.y * (s0.x - s2.x) + d2.y * (s1.x - s0.x)) / det;
  const e = (d0.x * (s1.x * s2.y - s2.x * s1.y) + d1.x * (s2.x * s0.y - s0.x * s2.y) + d2.x * (s0.x * s1.y - s1.x * s0.y)) / det;
  const f = (d0.y * (s1.x * s2.y - s2.x * s1.y) + d1.y * (s2.x * s0.y - s0.x * s2.y) + d2.y * (s0.x * s1.y - s1.x * s0.y)) / det;
  return { a, b, c, d, e, f };
}

function drawTriangle(ctx: CanvasRenderingContext2D, image: HTMLImageElement, src: [Point, Point, Point], dst: [Point, Point, Point]) {
  const matrix = computeAffineFromTriangles(src, dst);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(dst[0].x, dst[0].y);
  ctx.lineTo(dst[1].x, dst[1].y);
  ctx.lineTo(dst[2].x, dst[2].y);
  ctx.closePath();
  ctx.clip();
  ctx.transform(matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f);
  ctx.drawImage(image, 0, 0);
  ctx.restore();
}

function drawLayerPerspective(ctx: CanvasRenderingContext2D, image: HTMLImageElement, quad: [Point, Point, Point, Point], opacity: number) {
  const alpha = Math.max(0, Math.min(1, opacity));
  if (alpha <= 0) {
    return;
  }
  ctx.save();
  ctx.globalAlpha = alpha;
  const segments = 20;
  for (let y = 0; y < segments; y += 1) {
    for (let x = 0; x < segments; x += 1) {
      const u0 = x / segments;
      const u1 = (x + 1) / segments;
      const v0 = y / segments;
      const v1 = (y + 1) / segments;

      const p00 = bilinear(quad, u0, v0);
      const p10 = bilinear(quad, u1, v0);
      const p11 = bilinear(quad, u1, v1);
      const p01 = bilinear(quad, u0, v1);

      const sx0 = u0 * image.width;
      const sx1 = u1 * image.width;
      const sy0 = v0 * image.height;
      const sy1 = v1 * image.height;

      drawTriangle(
        ctx,
        image,
        [
          { x: sx0, y: sy0 },
          { x: sx1, y: sy0 },
          { x: sx1, y: sy1 }
        ],
        [p00, p10, p11]
      );

      drawTriangle(
        ctx,
        image,
        [
          { x: sx0, y: sy0 },
          { x: sx1, y: sy1 },
          { x: sx0, y: sy1 }
        ],
        [p00, p11, p01]
      );
    }
  }
  ctx.restore();
}

function drawLayerNormal(ctx: CanvasRenderingContext2D, image: HTMLImageElement, placement: Placement, opacity: number) {
  const alpha = Math.max(0, Math.min(1, opacity));
  if (alpha <= 0) {
    return;
  }
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(placement.x, placement.y);
  ctx.rotate((placement.rotation * Math.PI) / 180);
  // Keep canvas output consistent with Konva width/height semantics.
  ctx.drawImage(image, 0, 0, placement.width, placement.height);
  ctx.restore();
}

function toBlob(canvas: HTMLCanvasElement, format: ExportFormat, quality: number): Promise<Blob> {
  const mime = contentTypeForFormat(format);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Export failed"));
          return;
        }
        resolve(blob);
      },
      mime,
      quality
    );
  });
}

export async function composeOutput(
  templateImage: HTMLImageElement,
  layers: ComposeLayer[],
  format: ExportFormat,
  quality: number
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = templateImage.naturalWidth;
  canvas.height = templateImage.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("2D context unavailable");
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(templateImage, 0, 0, canvas.width, canvas.height);

  for (const layer of layers) {
    if (!layer.visible) {
      continue;
    }
    if (layer.geometry.quad) {
      drawLayerPerspective(ctx, layer.image, layer.geometry.quad, layer.opacity);
    } else {
      drawLayerNormal(ctx, layer.image, layer.geometry.placement, layer.opacity);
    }
  }

  return toBlob(canvas, format, quality);
}
